import { FunctionComponent, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useStore } from '@hub/helpers/use-store';
import { useColor } from '@hub/hooks';
import { canResendInvite, formatDate } from '@monorepo/client-common';
import { WithdrawPayload } from '@monorepo/client-types';
import { Icons } from '@monorepo/icons';
import {
  Button,
  Flex,
  TextComponent,
  Tooltip,
} from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  IconsNames,
  RISE_RESEND_INVITE_TIME_HOURS,
  ThemeColors,
} from '@monorepo/types';

interface Props {
  form: UseFormReturn<WithdrawPayload>;
}

const RiseWithdraw: FunctionComponent<Props> = ({ form }) => {
  const {
    dataStore: { userStore, payoutStore },
  } = useStore();
  const { getColor } = useColor();
  const { updateLoadingState, isLoading } = useLoading();
  const { t } = useTranslation(['errors', 'withdrawFunds']);

  const riseId = form.watch('metadata.riseId');
  const invitedAt = userStore.currentUser?.paymentData?.rise?.invitedAt;

  const canSendRiseInvite = canResendInvite(invitedAt);

  const handleSendInvite = async () => {
    try {
      if (!canSendRiseInvite) {
        return;
      }

      updateLoadingState(LoadingState.Loading);
      const response = await payoutStore.sendRiseInvitation();

      if (!response.success) {
        throw new Error('Failed sending rise invite');
      }

      userStore.updatePaymentData({
        rise: {
          invitedAt: response.invitedAt,
        },
      });

      message.success(t('withdrawFunds:rise_invitation_sent'));
    } catch (e) {
      console.error('Failed sending rise invite', e);
      message.error(t('errors:something_went_wrong'));
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const renderSentResult = () => {
    if (!invitedAt) {
      return null;
    }

    const tooltipText = t('withdrawFunds:invite_delay');
    const lastSentText = t('withdrawFunds:last_sent', {
      date: formatDate(invitedAt, { template: 'dateTimeShort' }),
      interpolation: { escapeValue: false },
    });

    return (
      <Tooltip title={tooltipText}>
        <Flex gap={3} align="center">
          <Icons iconName={IconsNames.QuestionCircle} />
          <TextComponent level={7} weight={500}>
            {lastSentText}
          </TextComponent>
        </Flex>
      </Tooltip>
    );
  };

  useEffect(() => {
    if (!riseId) {
      form.setError('metadata.riseId', {
        message: 'Rise id is required',
        type: 'manual',
      });
    }
    return () => {
      form.clearErrors('metadata.riseId');
    };
  }, [riseId]);

  if (riseId) {
    return (
      <TextComponent level={7} weight={600}>
        {t('withdrawFunds:your_rise_id', { id: riseId })}
      </TextComponent>
    );
  }

  return (
    <Flex vertical gap={5}>
      <TextComponent level={7} color={getColor(ThemeColors.colorError)}>
        {t('withdrawFunds:rise_id_is_required')}
      </TextComponent>

      <Button
        loading={isLoading}
        disabled={!canSendRiseInvite}
        onClick={handleSendInvite}
        type="default"
        size="small"
      >
        {t('withdrawFunds:send_me_rise_invite')}
      </Button>
      <Flex justify="space-between" wrap="wrap" gap={10}>
        <TextComponent level={7}>
          {t('withdrawFunds:send_invite_wait_time', {
            time: RISE_RESEND_INVITE_TIME_HOURS,
          })}
        </TextComponent>

        {renderSentResult()}
      </Flex>
    </Flex>
  );
};

export default observer(RiseWithdraw);
